import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { Context, Lijst } from '@xxtract/tabelfilters';
import { useQuarterlyReports, useUpdateReportConfig, useSendReport, useGenerateAllReports } from '../hooks/useQuarterlyReports';
import { ApiErrorMessage } from '@/components/feedback/ApiErrorMessage';
import { FilterRejectedMessage } from '@/components/feedback/FilterRejectedMessage';
import { Filterbalk, SorteerbareKop, ariaSort, useStartadres, useTabelData, useTabelStand } from '@/components/tabel';
import { useLijstStandStore } from '@/stores/lijstStandStore';
import { getQuarterOptions } from '../format';
import type { QuarterlyReportClient } from '../value.api';

type RapportStatus = 'sent' | 'ready' | 'noRecipient';
const STATUS_LABELS: Record<RapportStatus, string> = {
  sent: 'quarterlyReport.statusSent',
  ready: 'quarterlyReport.statusReady',
  noRecipient: 'quarterlyReport.statusNoRecipient',
};

/** The status as shown in the row: sent (or generated), ready (recipient known), or no recipient. */
function rapportStatus(client: QuarterlyReportClient): RapportStatus {
  if (client.status.status === 'sent' || client.status.status === 'generated') return 'sent';
  return client.config?.recipientEmail ? 'ready' : 'noRecipient';
}

/** Kolommen van kwartaalrapporten (spec-tabelfilters story 5); acties staan erbuiten. */
export const KWARTAALRAPPORTEN_LIJST: Lijst<QuarterlyReportClient> = {
  kolommen: [
    { sleutel: 'klant', kopSleutel: 'quarterlyReport.client', type: 'tekst', waarde: (c) => c.companyName },
    { sleutel: 'ontvanger', kopSleutel: 'quarterlyReport.recipient', type: 'tekst', waarde: (c) => c.config?.recipientName ?? null },
    { sleutel: 'email', kopSleutel: 'quarterlyReport.email', type: 'tekst', waarde: (c) => c.config?.recipientEmail ?? null },
    { sleutel: 'status', kopSleutel: 'common.status', type: 'keuzelijst', waarde: rapportStatus, waardeLabel: (w, t) => t(STATUS_LABELS[w as RapportStatus] ?? w) },
  ],
};
const STAND_SLEUTEL = 'kwartaalrapporten';
const FILTER_TEST_ID = 'quarterly-report';
/** Paginafilter: the chosen quarter lives in the list state (no address, no old link). */
const KWARTAAL_SLEUTEL = 'kwartaalrapporten.kwartaal';
const STARTADRES = [{ lijst: KWARTAALRAPPORTEN_LIJST, standSleutel: STAND_SLEUTEL }];

export function QuarterlyReportPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const quarterOptions = getQuarterOptions(4);
  const bewaardKwartaal = useLijstStandStore((s) => s.standen[KWARTAAL_SLEUTEL]?.filters.kwartaal);
  const gekozen = Array.isArray(bewaardKwartaal) ? bewaardKwartaal[0] : undefined;
  const quarter = quarterOptions.some((o) => o.value === gekozen) ? gekozen! : (quarterOptions[0]?.value ?? '');
  const setQuarter = (q: string) => useLijstStandStore.getState().zetPaginafilter(KWARTAAL_SLEUTEL, { kwartaal: [q] });
  const context = useMemo<Context>(() => ({ t }), [t]);
  const { start, geweigerd, wisGeweigerd } = useStartadres(STARTADRES);
  const stand = useTabelStand(KWARTAALRAPPORTEN_LIJST, STAND_SLEUTEL, { start: start[STAND_SLEUTEL], afhankelijkVan: KWARTAAL_SLEUTEL });
  const kopRef = useRef<HTMLHeadingElement>(null);
  const [editingGln, setEditingGln] = useState<string | null>(null);
  const [sendingGln, setSendingGln] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuarterlyReports(quarter);
  const tabel = useTabelData(data?.data, KWARTAALRAPPORTEN_LIJST, stand, context);
  const updateConfig = useUpdateReportConfig();
  const sendReport = useSendReport();
  const generateAll = useGenerateAllReports();

  if (isError) {
    return <ApiErrorMessage error={error} onRetry={() => refetch()} />;
  }

  const clients = data?.data ?? [];

  const handleSaveConfig = async (gln: string, e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    try {
      await updateConfig.mutateAsync({
        gln,
        config: {
          recipientName: fd.get('recipientName') as string,
          recipientEmail: fd.get('recipientEmail') as string,
        },
      });
      setEditingGln(null);
    } catch {
      // Error shown via mutation state
    }
  };

  const handleSend = async (gln: string) => {
    setSendingGln(gln);
    try {
      await sendReport.mutateAsync(gln);
    } catch {
      // Error shown via mutation state
    } finally {
      setSendingGln(null);
    }
  };

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 ref={kopRef} tabIndex={-1} className="outline-none" style={{ fontSize: '1.25rem', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--foreground-heading)' }}>
          {t('quarterlyReport.title')}
        </h1>
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={quarter}
            onChange={(e) => setQuarter(e.target.value)}
            aria-label={t('quarterlyReport.selectQuarter', 'Select quarter')}
            className="rounded-lg px-3 py-2 text-sm"
            style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
          >
            {quarterOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <button
            onClick={() => generateAll.mutate(quarter)}
            disabled={generateAll.isPending}
            className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-white"
            style={{ backgroundColor: 'var(--primary)' }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>auto_awesome</span>
            {generateAll.isPending ? t('common.loading') : t('quarterlyReport.generateAll')}
          </button>
        </div>
      </div>

      <FilterRejectedMessage
        baseTestId={FILTER_TEST_ID}
        focusNaWissen={kopRef}
        linkGeweigerd={geweigerd}
        onClear={() => {
          stand.reset();
          wisGeweigerd();
        }}
      />

      {!isLoading && clients.length > 0 && <Filterbalk context={context} data={tabel} lijst={KWARTAALRAPPORTEN_LIJST} stand={stand} />}

      {/* Table: scrolls in its own container, never the page (375 px) */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => <div key={i} className="skeleton h-14 rounded-lg" />)}
        </div>
      ) : clients.length === 0 ? (
        <div className="flex flex-col items-center py-16 text-center" style={{ color: 'var(--foreground-muted)' }}>
          <span className="material-symbols-outlined mb-3" style={{ fontSize: '48px' }}>summarize</span>
          <p className="text-lg font-semibold" style={{ color: 'var(--foreground)' }}>{t('quarterlyReport.empty')}</p>
        </div>
      ) : (
        <div className="rounded-xl overflow-x-auto" style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border-soft)', boxShadow: 'var(--card-shadow)' }}>
          <table className="w-full text-sm">
            <caption className="sr-only">{t('quarterlyReport.title')}</caption>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                {KWARTAALRAPPORTEN_LIJST.kolommen.map((k) => (
                  <th key={k.sleutel} aria-label={t(k.kopSleutel)} aria-sort={ariaSort(stand.sortering, k.sleutel)} className="px-4 py-3 text-left font-semibold" style={{ color: 'var(--foreground)' }}>
                    <SorteerbareKop kolom={k} onKlik={() => stand.klikOpKop(k.sleutel)} sortering={stand.sortering} />
                  </th>
                ))}
                <th className="px-4 py-3 text-right font-semibold" style={{ color: 'var(--foreground)' }}>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {tabel.rijen.length === 0 && (
                <tr>
                  <td colSpan={KWARTAALRAPPORTEN_LIJST.kolommen.length + 1} className="px-4 py-8 text-center" style={{ color: 'var(--foreground-muted)' }}>{t('common.noResults')}</td>
                </tr>
              )}
              {tabel.rijen.map((client) => (
                <ReportRow
                  key={client.clientGln}
                  client={client}
                  isEditing={editingGln === client.clientGln}
                  onEdit={() => setEditingGln(client.clientGln)}
                  onCancelEdit={() => setEditingGln(null)}
                  onSaveConfig={(e) => handleSaveConfig(client.clientGln, e)}
                  onPreview={() => navigate(`/reports/quarterly/${client.clientGln}`)}
                  onSend={() => handleSend(client.clientGln)}
                  isSending={sendingGln === client.clientGln}
                  t={t}
                />
              ))}
            </tbody>
          </table>
          <style>{`.report-row:hover { background-color: var(--background-muted); }`}</style>
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs" style={{ color: 'var(--foreground-muted)' }}>
        <span className="flex items-center gap-1"><span className="material-symbols-outlined" style={{ fontSize: '14px', color: 'var(--success-dark)' }}>check_circle</span>{t('quarterlyReport.statusSent')}</span>
        <span className="flex items-center gap-1"><span className="material-symbols-outlined" style={{ fontSize: '14px', color: 'var(--primary)' }}>draft</span>{t('quarterlyReport.statusReady')}</span>
        <span className="flex items-center gap-1"><span className="material-symbols-outlined" style={{ fontSize: '14px', color: 'var(--warning)' }}>warning</span>{t('quarterlyReport.statusNoRecipient')}</span>
      </div>
    </div>
  );
}

function ReportRow({
  client, isEditing, onEdit, onCancelEdit, onSaveConfig, onPreview, onSend, isSending, t,
}: {
  client: QuarterlyReportClient;
  isEditing: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSaveConfig: (e: React.FormEvent<HTMLFormElement>) => void;
  onPreview: () => void;
  onSend: () => void;
  isSending: boolean;
  t: (key: string, params?: Record<string, unknown>) => string;
}) {
  const hasRecipient = !!client.config?.recipientEmail;
  const status = rapportStatus(client);
  const statusConfig = status === 'sent'
    ? { icon: 'check_circle', color: 'var(--success-dark)', bg: 'var(--success-light)', label: t('quarterlyReport.statusSent') }
    : status === 'ready'
    ? { icon: 'draft', color: 'var(--primary)', bg: 'var(--primary-light)', label: t('quarterlyReport.statusReady') }
    : { icon: 'warning', color: 'var(--warning)', bg: 'var(--warning-light)', label: t('quarterlyReport.statusNoRecipient') };

  if (isEditing) {
    return (
      <tr style={{ borderBottom: '1px solid var(--border-soft)' }}>
        <td colSpan={KWARTAALRAPPORTEN_LIJST.kolommen.length + 1} className="px-4 py-3">
          <form onSubmit={onSaveConfig} className="flex items-end gap-3">
            <span className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>{client.companyName}</span>
            <label className="sr-only" htmlFor={`rn-${client.clientGln}`}>{t('quarterlyReport.recipientName')}</label>
            <input id={`rn-${client.clientGln}`} name="recipientName" placeholder={t('quarterlyReport.recipientName')} defaultValue={client.config?.recipientName ?? ''} className="rounded border px-2 py-1 text-sm" style={{ borderColor: 'var(--border)', width: '150px' }} required />
            <label className="sr-only" htmlFor={`re-${client.clientGln}`}>{t('quarterlyReport.recipientEmail')}</label>
            <input id={`re-${client.clientGln}`} name="recipientEmail" type="email" placeholder={t('quarterlyReport.recipientEmail')} defaultValue={client.config?.recipientEmail ?? ''} className="rounded border px-2 py-1 text-sm" style={{ borderColor: 'var(--border)', width: '200px' }} required />
            <button type="submit" className="rounded px-2.5 py-1 text-xs font-medium text-white" style={{ backgroundColor: 'var(--primary)' }}>{t('common.save')}</button>
            <button type="button" onClick={onCancelEdit} className="text-xs" style={{ color: 'var(--foreground-muted)' }}>{t('common.cancel')}</button>
          </form>
        </td>
      </tr>
    );
  }

  return (
    <tr className="report-row" style={{ borderBottom: '1px solid var(--border-soft)' }}>
      <td className="px-4 py-3 font-medium" style={{ color: 'var(--foreground)' }}>{client.companyName}</td>
      <td className="px-4 py-3" style={{ color: 'var(--foreground-muted)' }}>{client.config?.recipientName ?? '—'}</td>
      <td className="px-4 py-3" style={{ color: hasRecipient ? 'var(--foreground-muted)' : 'var(--warning)' }}>{client.config?.recipientEmail ?? '—'}</td>
      <td className="px-4 py-3">
        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.6875rem] font-medium" style={{ backgroundColor: statusConfig.bg, color: statusConfig.color }}>
          <span className="material-symbols-outlined" style={{ fontSize: '12px' }}>{statusConfig.icon}</span>
          {statusConfig.label}
        </span>
      </td>
      <td className="px-4 py-3 text-right">
        <div className="flex justify-end gap-1">
          <button onClick={onPreview} disabled={!hasRecipient} aria-label={t('quarterlyReport.preview')} className="rounded p-1.5" style={{ color: hasRecipient ? 'var(--foreground-muted)' : 'var(--border)' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>visibility</span>
          </button>
          <button onClick={onEdit} aria-label={t('quarterlyReport.configure')} className="rounded p-1.5" style={{ color: 'var(--foreground-muted)' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>settings</span>
          </button>
          {hasRecipient && (
            <button onClick={onSend} disabled={isSending} aria-label={t('quarterlyReport.send')} className="rounded p-1.5" style={{ color: 'var(--primary)' }}>
              <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>send</span>
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
